import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Modal, TextInput, Button, Group, Stack, Switch } from "@mantine/core";
import {
  IconCalendarCheck,
  IconCalendarPlus,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconFlag,
  IconListDetails,
  IconMessageCircle,
  IconPlus,
  IconSearch,
  IconSortAscending,
  IconSortDescending,
  IconUserCheck,
  IconUserPlus,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import RecruitDetailView from "../Recruiting/RecruitDetailView";
import LogContactModal from "../Recruiting/LogContactModal";
import CalendarScheduler from "../Recruiting/CalendarScheduler";
import AddToListModal from "../Recruiting/AddToListModal";
import NewLeadModal from "./NewLeadModal";
import AddRepFromLeadModal, { leadName } from "./AddRepFromLeadModal";
import "./LeadsTab.css";

// Lead `status` values from GET /v2/recruiting/pipeline, in funnel order
const FUNNEL = [
  "Uncontacted",
  "Lead",
  "Initial Meeting Scheduled",
  "Meeting",
  "Offers Sent",
  "Signed",
];

const STAGE_LABELS = {
  Uncontacted: "Uncontacted",
  Lead: "Contacted",
  "Initial Meeting Scheduled": "1st Meeting Set",
  Meeting: "Meeting",
  "Offers Sent": "Offers Sent",
  Signed: "Signed",
  Cold: "Cold",
};

// Stage dot colors, the same hues iOS uses per stage
const STAGE_COLORS = {
  Uncontacted: "#776f60",
  Lead: "#3b4da8",
  "Initial Meeting Scheduled": "#b5832a",
  Meeting: "#7550a8",
  "Offers Sent": "var(--color-primary)",
  Signed: "#28a745",
  Cold: "#7baabf",
};

// The status picker (PATCH /v2/recruiting/leads/:id/status). Meetings and
// Signed aren't offered: a lead gets there by booking one or being added as a rep.
const STATUS_OPTIONS = [
  { key: "uncontacted", status: "Uncontacted", label: "Uncontacted" },
  { key: "contacted", status: "Lead", label: "Contacted" },
  { key: "offer_sent", status: "Offers Sent", label: "Offers Sent" },
  { key: "cold", status: "Cold", label: "Cold" },
];

// The header stat cards, each also a filter. Cold is a shelf rather than a
// step, so it's a toolbar toggle instead of a card.
const STAT_CARDS = [
  { id: "lead", label: "Active Leads", color: "#3b4da8" },
  { id: "meeting", label: "Meeting Set", color: "#7550a8" },
  { id: "offer", label: "Offer Extended", color: "var(--color-primary)" },
  { id: "signed", label: "Hired", color: "#28a745" },
];

const LIST_COLORS = [
  "var(--color-primary)",
  "#28a745",
  "#3b4da8",
  "#7550a8",
  "#b5832a",
  "#3dbf8a",
];
const listColor = id => LIST_COLORS[Math.abs(id) % LIST_COLORS.length];

const DAY_MS = 24 * 60 * 60 * 1000;

const parseDate = value => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const ordinal = n => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  return `${n}${{ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"}`;
};

const stageLabel = lead => {
  if (lead.status === "Meeting" && lead.meeting_number > 0) {
    return `${ordinal(lead.meeting_number)} Meeting`;
  }
  return STAGE_LABELS[lead.status] || lead.status;
};

const stageOrder = lead => {
  const index = FUNNEL.indexOf(lead.status);
  return index === -1 ? 99 : index;
};

const formatMeeting = date =>
  date.toLocaleDateString(undefined, { month: "short", day: "numeric" });

const formatMeetingTime = date =>
  date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

function LeadsTab() {
  const { getVerifiedJWT } = useAuth();
  const { showError } = useToast();

  // GET /v2/recruiting/pipeline for the current scope
  const [pipeline, setPipeline] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Whose pipeline: null is the caller's own, else { id, name } from manager_list
  const [scope, setScope] = useState(null);
  const [includeDownline, setIncludeDownline] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  // The caller's own count for the "My Pipeline" row; manager_list leaves them out
  const [myLeadCount, setMyLeadCount] = useState(null);

  const [stageFilters, setStageFilters] = useState(() => new Set());
  const [showCold, setShowCold] = useState(false);
  const [selectedListIds, setSelectedListIds] = useState(() => new Set());
  const [search, setSearch] = useState("");
  // Last Contact, oldest first: the lead gone longest without contact on top
  const [sort, setSort] = useState({ field: "lastContact", ascending: true });
  const [expandedId, setExpandedId] = useState(null);

  const [detailLead, setDetailLead] = useState(null);
  const [showAddLead, setShowAddLead] = useState(false);
  // Null when closed; `{ lead }` (lead may be null) when open
  const [addRep, setAddRep] = useState(null);
  const [contactLead, setContactLead] = useState(null);
  const [scheduleLead, setScheduleLead] = useState(null);
  const [listLead, setListLead] = useState(null);
  const [statusLead, setStatusLead] = useState(null);
  const [showNewList, setShowNewList] = useState(false);
  const [newListName, setNewListName] = useState("");

  // Bumped per pipeline load so a slow response for an old scope can't win
  const requestRef = useRef(0);
  const scopeRef = useRef(null);

  const authedFetch = useCallback(
    async (path, options = {}) => {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      return fetch(getApiEndpoint(path), {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
          ...options.headers,
        },
      });
    },
    [getVerifiedJWT]
  );

  const loadPipeline = useCallback(async () => {
    const request = ++requestRef.current;
    const params = new URLSearchParams();
    if (scope) params.set("manager_user_id", scope.id);
    if (includeDownline) params.set("include_downline", "true");
    try {
      const response = await authedFetch(
        `/v2/recruiting/pipeline?${params.toString()}`
      );
      if (!response.ok) throw new Error("Failed to load leads");
      const data = (await response.json()).data;
      if (request !== requestRef.current) return;
      setPipeline(data);
      setError(null);
      if (!scope && !includeDownline) {
        setMyLeadCount(data.pipeline_totals?.total_leads ?? null);
      }
    } catch (err) {
      if (request === requestRef.current) {
        setError(err.message || "Failed to load leads");
      }
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, [authedFetch, scope, includeDownline]);

  const loadMeetings = useCallback(async () => {
    try {
      const response = await authedFetch("/v1/recruiting/upcoming-meetings");
      if (response.ok) setMeetings((await response.json()).data || []);
    } catch {
      // Meetings only fill the date column
    }
  }, [authedFetch]);

  const loadLists = useCallback(async () => {
    try {
      const response = await authedFetch("/v2/recruiting/lists");
      if (!response.ok) return;
      const fetched = (await response.json()).data || [];
      setLists(fetched);
      // Drop any selection whose list no longer exists
      setSelectedListIds(
        prev => new Set([...prev].filter(id => fetched.some(l => l.id === id)))
      );
    } catch {
      // The list filter just stays empty
    }
  }, [authedFetch]);

  const refreshAll = useCallback(() => {
    loadPipeline();
    loadMeetings();
    loadLists();
  }, [loadPipeline, loadMeetings, loadLists]);

  useEffect(() => {
    loadMeetings();
    loadLists();
  }, [loadMeetings, loadLists]);

  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  // Close the scope dropdown on an outside click
  useEffect(() => {
    if (!scopeOpen) return undefined;
    const close = event => {
      if (!scopeRef.current?.contains(event.target)) setScopeOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [scopeOpen]);

  // Lead owners by user id, so downline leads can show and be searched by owner
  const ownerNames = useMemo(() => {
    const names = new Map();
    (pipeline?.manager_list || []).forEach(m =>
      names.set(m.id, [m.first_name, m.last_name].filter(Boolean).join(" "))
    );
    return names;
  }, [pipeline]);

  const meetingTimes = useMemo(() => {
    const byLead = new Map();
    meetings.forEach(m => {
      const date = parseDate(m.time);
      if (!date) return;
      if (!byLead.has(m.lead_id)) byLead.set(m.lead_id, []);
      byLead.get(m.lead_id).push(date);
    });
    return byLead;
  }, [meetings]);

  const now = Date.now();

  // The pipeline's next meeting plus the meetings feed, so a just-booked
  // meeting shows before the refetch lands
  const nextMeeting = useCallback(
    lead => {
      const dates = [
        ...(meetingTimes.get(lead.id) || []),
        parseDate(lead.next_meeting_at),
      ].filter(date => date && date.getTime() >= now);
      if (dates.length === 0) return null;
      return new Date(Math.min(...dates.map(d => d.getTime())));
    },
    [meetingTimes, now]
  );

  // Latest of the last logged contact, the last reported meeting and any
  // scheduled meeting that has since passed
  const lastTouch = useCallback(
    lead => {
      const dates = [
        parseDate(lead.last_contact_date),
        parseDate(lead.last_meeting_date),
        ...(meetingTimes.get(lead.id) || []).filter(d => d.getTime() < now),
      ].filter(Boolean);
      if (dates.length === 0) return null;
      return new Date(Math.max(...dates.map(d => d.getTime())));
    },
    [meetingTimes, now]
  );

  const daysSinceContact = lead => {
    const touched = lastTouch(lead);
    if (!touched) return null;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfTouch = new Date(touched);
    startOfTouch.setHours(0, 0, 0, 0);
    return Math.max(0, Math.round((startOfToday - startOfTouch) / DAY_MS));
  };

  const inSelectedLists = useCallback(
    lead =>
      selectedListIds.size === 0 ||
      (lead.list_ids || []).some(id => selectedListIds.has(id)),
    [selectedListIds]
  );

  const pipelineLeads = useMemo(
    () => (pipeline?.pipeline_leads || []).filter(inSelectedLists),
    [pipeline, inSelectedLists]
  );
  const hiredLeads = useMemo(
    () => (pipeline?.hired_leads || []).filter(inSelectedLists),
    [pipeline, inSelectedLists]
  );
  const coldLeads = useMemo(
    () => (pipeline?.cold_leads || []).filter(inSelectedLists),
    [pipeline, inSelectedLists]
  );

  const activeLeads = useMemo(
    () =>
      pipelineLeads.filter(l => l.status !== "Cold" && l.status !== "Signed"),
    [pipelineLeads]
  );

  const hasFutureMeeting = useCallback(
    lead => Boolean(nextMeeting(lead)),
    [nextMeeting]
  );

  // Header numbers. Active Leads and Meeting Set are always about the active
  // leads on screen; the rest come from the API unless a list filter is on.
  const totals = useMemo(() => {
    const apiTotals = pipeline?.pipeline_totals;
    const filtered = selectedListIds.size > 0 || !apiTotals;
    return {
      lead: activeLeads.length,
      meeting: activeLeads.filter(hasFutureMeeting).length,
      offer: filtered
        ? pipelineLeads.filter(l => l.status === "Offers Sent").length
        : apiTotals.offer_extended,
      signed: filtered ? hiredLeads.length : apiTotals.converted_leads,
    };
  }, [
    pipeline,
    selectedListIds,
    activeLeads,
    pipelineLeads,
    hiredLeads,
    hasFutureMeeting,
  ]);

  const coldListSelected = lists.some(
    l => l.is_cold_list && selectedListIds.has(l.id)
  );

  const shownLeads = useMemo(() => {
    let base;
    if (stageFilters.size === 0 && !showCold) {
      // The default view: active leads, plus the Cold Leads list's members
      // when that list is picked
      base = coldListSelected ? [...activeLeads, ...coldLeads] : activeLeads;
    } else {
      base = activeLeads.filter(lead => {
        if (stageFilters.has("lead")) {
          if (lead.status === "Uncontacted" || lead.status === "Lead") {
            return true;
          }
        }
        // Meeting means one booked in the future, whatever the stage, the
        // same set the Meeting Set card counts
        if (stageFilters.has("meeting") && hasFutureMeeting(lead)) return true;
        if (stageFilters.has("offer") && lead.status === "Offers Sent") {
          return true;
        }
        return false;
      });
      if (stageFilters.has("signed")) base = [...base, ...hiredLeads];
      if (showCold) base = [...base, ...coldLeads];
    }
    const seen = new Set();
    base = base.filter(lead => !seen.has(lead.id) && seen.add(lead.id));

    const query = search.trim().toLowerCase();
    if (query) {
      base = base.filter(
        lead =>
          leadName(lead).toLowerCase().includes(query) ||
          (lead.email || "").toLowerCase().includes(query) ||
          (ownerNames.get(lead.manager_user_id) || "")
            .toLowerCase()
            .includes(query)
      );
    }

    // Newest first within a tie, so a fresh lead tops its stage
    const newerFirst = (a, b) =>
      (parseDate(b.created_at)?.getTime() ?? 0) -
      (parseDate(a.created_at)?.getTime() ?? 0);
    const direction = sort.ascending ? 1 : -1;
    // Missing values always sink, whichever way the column sorts
    const byDate = (a, b, dateOf) => {
      const x = dateOf(a)?.getTime();
      const y = dateOf(b)?.getTime();
      if (x == null && y == null) return stageOrder(a) - stageOrder(b);
      if (x == null) return 1;
      if (y == null) return -1;
      return x === y ? stageOrder(a) - stageOrder(b) : (x - y) * direction;
    };

    return [...base].sort((a, b) => {
      switch (sort.field) {
        case "name":
          return (
            leadName(a).localeCompare(leadName(b), undefined, {
              sensitivity: "base",
            }) * direction || stageOrder(a) - stageOrder(b)
          );
        case "stage":
          return (
            (stageOrder(a) - stageOrder(b)) * direction || newerFirst(a, b)
          );
        case "nextMeeting":
          return byDate(a, b, nextMeeting);
        case "lastContact":
        default:
          return byDate(a, b, lastTouch);
      }
    });
  }, [
    stageFilters,
    showCold,
    coldListSelected,
    activeLeads,
    coldLeads,
    hiredLeads,
    hasFutureMeeting,
    search,
    ownerNames,
    sort,
    nextMeeting,
    lastTouch,
  ]);

  const toggleStage = id =>
    setStageFilters(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleList = id =>
    setSelectedListIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSort = field =>
    setSort(prev =>
      prev.field === field
        ? { field, ascending: !prev.ascending }
        : { field, ascending: true }
    );

  const pickScope = next => {
    setScopeOpen(false);
    setLoading(true);
    setScope(next);
  };

  const changeStatus = async (lead, option) => {
    setStatusLead(null);
    try {
      const response = await authedFetch(
        `/v2/recruiting/leads/${lead.id}/status`,
        { method: "PATCH", body: JSON.stringify({ status: option.key }) }
      );
      if (!response.ok) throw new Error();
    } catch {
      showError(
        `${leadName(lead)} couldn't be moved to ${option.label}. Please try again.`
      );
    }
    // Reload either way, so the page shows where the lead actually is
    loadPipeline();
  };

  const createList = async () => {
    const name = newListName.trim();
    if (!name) return;
    setShowNewList(false);
    setNewListName("");
    try {
      const response = await authedFetch("/v2/recruiting/lists", {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      if (!response.ok) throw new Error();
      loadLists();
    } catch {
      showError("Couldn't create the list. Please try again.");
    }
  };

  if (detailLead) {
    return (
      <RecruitDetailView
        recruit={{ ...detailLead, last_name: detailLead.last_name || "" }}
        onBack={() => {
          setDetailLead(null);
          refreshAll();
        }}
        onSendOffer={() => setAddRep({ lead: detailLead })}
      />
    );
  }

  const scopeLabel = scope ? `${scope.name}'s Pipeline` : "My Pipeline";
  const managers = pipeline?.manager_list || [];

  const renderHeader = () => (
    <div className="leads-tab-header">
      <div className="leads-tab-scope-card" ref={scopeRef}>
        <div className="leads-tab-scope-row">
          <button
            type="button"
            className="leads-tab-scope-button"
            onClick={() => setScopeOpen(open => !open)}
            aria-expanded={scopeOpen}
          >
            <span className="leads-tab-scope-title">{scopeLabel}</span>
            <IconChevronDown
              size={18}
              stroke={2}
              className={scopeOpen ? "leads-tab-rotate" : undefined}
              aria-hidden="true"
            />
          </button>
          {managers.length > 0 && (
            <Switch
              label="Downline"
              size="sm"
              checked={includeDownline}
              onChange={e => {
                setLoading(true);
                setIncludeDownline(e.currentTarget.checked);
              }}
            />
          )}
        </div>
        {scopeOpen && (
          <ul className="leads-tab-scope-menu">
            <li>
              <button
                type="button"
                className="leads-tab-scope-option"
                onClick={() => pickScope(null)}
              >
                <span>My Pipeline</span>
                {myLeadCount != null && (
                  <span className="leads-tab-scope-count">{myLeadCount}</span>
                )}
                {!scope && <IconCheck size={16} aria-hidden="true" />}
              </button>
            </li>
            {managers.map(m => {
              const name = [m.first_name, m.last_name]
                .filter(Boolean)
                .join(" ");
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    className="leads-tab-scope-option"
                    onClick={() => pickScope({ id: m.id, name })}
                  >
                    <span>{name}</span>
                    <span className="leads-tab-scope-count">
                      {m.total_leads}
                    </span>
                    {scope?.id === m.id && (
                      <IconCheck size={16} aria-hidden="true" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="leads-tab-stats">
        {STAT_CARDS.map(card => {
          const on = stageFilters.has(card.id);
          return (
            <button
              key={card.id}
              type="button"
              className={`leads-tab-stat${on ? " is-on" : ""}${
                card.id === "signed" ? " is-hired" : ""
              }`}
              style={{ "--stat-color": card.color }}
              onClick={() => toggleStage(card.id)}
              aria-pressed={on}
            >
              <span className="leads-tab-stat-value">{totals[card.id]}</span>
              <span className="leads-tab-stat-label">{card.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderToolbar = () => (
    <div className="leads-tab-toolbar">
      <div className="leads-tab-search">
        <IconSearch size={16} aria-hidden="true" />
        <input
          type="search"
          placeholder="Search leads"
          value={search}
          onChange={e => setSearch(e.target.value)}
          aria-label="Search leads"
        />
      </div>
      <div className="leads-tab-actions">
        <button
          type="button"
          className="leads-tab-button leads-tab-button-secondary"
          onClick={() => setShowAddLead(true)}
        >
          <IconUserPlus size={16} stroke={2} aria-hidden="true" />
          Add Lead
        </button>
        <button
          type="button"
          className="leads-tab-button"
          onClick={() => setAddRep({ lead: null })}
        >
          <IconUserCheck size={16} stroke={2} aria-hidden="true" />
          Add Rep
        </button>
      </div>
    </div>
  );

  const renderListFilter = () => (
    <div className="leads-tab-chips">
      <button
        type="button"
        className={`leads-tab-chip${
          selectedListIds.size === 0 ? " is-on" : ""
        }`}
        onClick={() => setSelectedListIds(new Set())}
      >
        All
      </button>
      {lists.map(list => (
        <button
          key={list.id}
          type="button"
          className={`leads-tab-chip${
            selectedListIds.has(list.id) ? " is-on" : ""
          }`}
          style={{ "--chip-color": listColor(list.id) }}
          onClick={() => toggleList(list.id)}
        >
          <span className="leads-tab-chip-dot" aria-hidden="true" />
          {list.name}
        </button>
      ))}
      <button
        type="button"
        className="leads-tab-chip leads-tab-chip-add"
        onClick={() => setShowNewList(true)}
      >
        <IconPlus size={14} stroke={2.25} aria-hidden="true" />
        New list
      </button>
      <span className="leads-tab-chip-divider" aria-hidden="true" />
      <button
        type="button"
        className={`leads-tab-chip${showCold ? " is-on" : ""}`}
        style={{ "--chip-color": STAGE_COLORS.Cold }}
        onClick={() => setShowCold(on => !on)}
        aria-pressed={showCold}
      >
        Cold
      </button>
    </div>
  );

  const sortHeader = (field, label, className) => {
    const active = sort.field === field;
    const Icon = sort.ascending ? IconSortAscending : IconSortDescending;
    return (
      <button
        type="button"
        className={`leads-tab-sort ${className}${active ? " is-active" : ""}`}
        onClick={() => toggleSort(field)}
      >
        {label}
        {active && <Icon size={14} aria-hidden="true" />}
      </button>
    );
  };

  const renderRow = lead => {
    const expanded = expandedId === lead.id;
    const next = nextMeeting(lead);
    const days = daysSinceContact(lead);
    const owner = ownerNames.get(lead.manager_user_id);
    const showOwner = (includeDownline || scope) && owner;
    const isActive = lead.status !== "Signed" && lead.status !== "Cold";

    return (
      <li key={lead.id} className="leads-tab-row">
        <div className="leads-tab-row-main">
          <button
            type="button"
            className="leads-tab-ident"
            onClick={() => setDetailLead(lead)}
          >
            <span className="leads-tab-name">
              {leadName(lead) || "Unnamed lead"}
              {lead.unreported_meetings > 0 && (
                <span
                  className="leads-tab-report-dot"
                  title="Meeting needs reporting"
                />
              )}
            </span>
            <span className="leads-tab-sub">
              <span
                className="leads-tab-stage-dot"
                style={{ background: STAGE_COLORS[lead.status] }}
                aria-hidden="true"
              />
              {stageLabel(lead)}
              {showOwner && <span> · {owner}</span>}
            </span>
          </button>

          <button
            type="button"
            className={`leads-tab-cell leads-tab-col-meeting${
              next ? " is-booked" : ""
            }`}
            onClick={() => setScheduleLead(lead)}
            title={next ? "Meeting scheduled" : "Schedule a meeting"}
          >
            {next ? (
              <>
                <span className="leads-tab-cell-value">
                  <IconCalendarCheck size={16} aria-hidden="true" />
                  {formatMeeting(next)}
                </span>
                <span className="leads-tab-cell-sub">
                  {formatMeetingTime(next)}
                </span>
              </>
            ) : (
              <>
                <span className="leads-tab-cell-empty">Set</span>
                <span className="leads-tab-cell-sub">Meeting</span>
              </>
            )}
          </button>

          <button
            type="button"
            className="leads-tab-cell leads-tab-col-contact"
            onClick={() => setContactLead(lead)}
            title="Log contact"
          >
            {days != null ? (
              <>
                <span
                  className={`leads-tab-cell-value${
                    days >= 7 && isActive ? " is-stale" : ""
                  }`}
                >
                  {days === 0 ? "Today" : days}
                </span>
                <span className="leads-tab-cell-sub">
                  {days === 0 ? " " : days === 1 ? "day" : "days"}
                </span>
              </>
            ) : (
              <>
                <span className="leads-tab-cell-empty">Log</span>
                <span className="leads-tab-cell-sub">Contact</span>
              </>
            )}
          </button>

          <button
            type="button"
            className={`leads-tab-expand${expanded ? " is-open" : ""}`}
            onClick={() => setExpandedId(expanded ? null : lead.id)}
            aria-label={expanded ? "Hide actions" : "Show actions"}
            aria-expanded={expanded}
          >
            <IconChevronRight size={18} stroke={2} />
          </button>
        </div>

        {expanded && (
          <div className="leads-tab-row-actions">
            {[
              {
                label: "Status",
                icon: IconFlag,
                color: "#c99a06",
                onClick: () => setStatusLead(lead),
              },
              {
                label: "List",
                icon: IconListDetails,
                color: "#3b85c8",
                onClick: () => setListLead(lead),
              },
              {
                label: "Contact",
                icon: IconMessageCircle,
                color: "#3dbf8a",
                onClick: () => setContactLead(lead),
              },
              {
                label: "Meeting",
                icon: IconCalendarPlus,
                color: "#9b7fd4",
                onClick: () => setScheduleLead(lead),
              },
              {
                label: "Add Rep",
                icon: IconUserPlus,
                color: "var(--color-primary)",
                onClick: () => setAddRep({ lead }),
                hidden: lead.status === "Signed",
              },
            ]
              .filter(action => !action.hidden)
              .map(({ label, icon: Icon, color, onClick }) => (
                <button
                  key={label}
                  type="button"
                  className="leads-tab-action"
                  style={{ "--action-color": color }}
                  onClick={onClick}
                >
                  <span className="leads-tab-action-circle">
                    <Icon size={20} stroke={2} aria-hidden="true" />
                  </span>
                  {label}
                </button>
              ))}
          </div>
        )}
      </li>
    );
  };

  const renderList = () => {
    if (loading && !pipeline) {
      return <div className="leads-tab-state">Loading leads...</div>;
    }
    if (error && !pipeline) {
      return (
        <div className="leads-tab-state leads-tab-state-error">
          {error}
          <button
            type="button"
            className="leads-tab-button"
            onClick={() => {
              setLoading(true);
              refreshAll();
            }}
          >
            Try Again
          </button>
        </div>
      );
    }
    return (
      <div className={`leads-tab-list-card${loading ? " is-loading" : ""}`}>
        <div className="leads-tab-list-head">
          {sortHeader("name", "Name", "leads-tab-col-name")}
          {sortHeader("nextMeeting", "Meeting", "leads-tab-col-meeting")}
          {sortHeader("lastContact", "Last contact", "leads-tab-col-contact")}
          <span className="leads-tab-expand-spacer" aria-hidden="true" />
        </div>
        {shownLeads.length === 0 ? (
          <div className="leads-tab-empty">
            <IconUserPlus size={22} aria-hidden="true" />
            <span className="leads-tab-empty-title">
              {stageFilters.size || showCold || search || selectedListIds.size
                ? "No leads match"
                : "No active leads"}
            </span>
            <span className="leads-tab-empty-sub">
              Add Lead to start your pipeline
            </span>
          </div>
        ) : (
          <ul className="leads-tab-rows">{shownLeads.map(renderRow)}</ul>
        )}
      </div>
    );
  };

  return (
    <div className="leads-tab">
      {renderHeader()}
      {renderToolbar()}
      {renderListFilter()}
      {renderList()}

      <NewLeadModal
        opened={showAddLead}
        managers={managers}
        onClose={() => setShowAddLead(false)}
        onAdded={refreshAll}
      />

      <AddRepFromLeadModal
        opened={Boolean(addRep)}
        lead={addRep?.lead || null}
        onClose={() => setAddRep(null)}
        onAdded={() => {
          // The lead is converted, so leave its profile for the list
          setDetailLead(null);
          refreshAll();
        }}
      />

      <LogContactModal
        isOpen={Boolean(contactLead)}
        onClose={() => setContactLead(null)}
        recruit={contactLead}
        onContactLogged={refreshAll}
      />

      <CalendarScheduler
        isOpen={Boolean(scheduleLead)}
        onClose={() => {
          setScheduleLead(null);
          refreshAll();
        }}
        recruit={scheduleLead}
      />

      <AddToListModal
        isOpen={Boolean(listLead)}
        onClose={() => setListLead(null)}
        recruit={listLead}
        onSuccess={refreshAll}
      />

      <Modal
        opened={Boolean(statusLead)}
        onClose={() => setStatusLead(null)}
        title="Change status"
        centered
        size="sm"
      >
        {statusLead && (
          <Stack gap="xs">
            <span className="leads-tab-status-note">
              {leadName(statusLead)} is {stageLabel(statusLead)}. Move them to:
            </span>
            {STATUS_OPTIONS.filter(o => o.status !== statusLead.status).map(
              option => (
                <Button
                  key={option.key}
                  variant="default"
                  fullWidth
                  onClick={() => changeStatus(statusLead, option)}
                  leftSection={
                    <span
                      className="leads-tab-stage-dot"
                      style={{ background: STAGE_COLORS[option.status] }}
                    />
                  }
                >
                  {option.label}
                </Button>
              )
            )}
          </Stack>
        )}
      </Modal>

      <Modal
        opened={showNewList}
        onClose={() => setShowNewList(false)}
        title="New List"
        centered
        size="sm"
      >
        <Stack gap="md">
          <TextInput
            label="List name"
            placeholder="Group recruits into a named list"
            value={newListName}
            onChange={e => setNewListName(e.currentTarget.value)}
            onKeyDown={e => e.key === "Enter" && createList()}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setShowNewList(false)}>
              Cancel
            </Button>
            <Button onClick={createList} disabled={!newListName.trim()}>
              Create
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}

export default LeadsTab;
